import React, { useState } from 'react';
import { Panel } from 'primereact/panel';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';

const InputDataCard = ({
    inputData,
    inputSummary,
    reportType,
    submittedAt,
    className = '',
    showExpandButton = true,
    defaultExpanded = false
}) => {
    const [expanded, setExpanded] = useState(defaultExpanded);

    // Always render the card, even if no input data
    const hasInputData = inputData && Object.keys(inputData).length > 0;

    // Helper function to render field value based on type
    const renderFieldValue = (key, value, index) => {
        if (!value || value === '') return null;

        // Skip technical fields that shouldn't be displayed
        if (key === 'consent' || key === 'application_id') return null;

        // Determine column size based on report type
        const isEquifax = reportType === 'Equifax' ||
                         reportType === 'BUREAU_REPORT' ||
                         reportType === 'Equifax Bureau Report';

        const isExperian = reportType === 'Experian' ||
                          reportType === 'Experian Bureau Report' ||
                          reportType === 'EXPERIAN_BUREAU_REPORT';

        let columnClass = 'field col-12 md:col-6'; // Default: 2 columns

        if (isEquifax) {
            // Equifax: First 5 fields in row 1 (5 columns), next 2 fields in row 2 (2 columns)
            // Using custom width for 5 columns: 12/5 = 2.4, so we'll use flex with percentage
            if (index < 5) {
                columnClass = 'field col-12'; // Will add custom style for 20% width
            } else {
                columnClass = 'field col-12 md:col-6'; // 2 columns
            }
        } else if (isExperian) {
            // Experian: First 4 fields in row 1 (4 columns), next 3 fields in row 2 (3 columns)
            if (index < 4) {
                columnClass = 'field col-12 md:col-3'; // 4 columns
            } else {
                columnClass = 'field col-12 md:col-4'; // 3 columns
            }
        }

        // Common field mappings for display
        const fieldLabels = {
            mobile: 'Mobile Number',
            mobileNumber: 'Mobile Number',
            mobile_no: 'Mobile Number',
            phone: 'Phone Number',
            pan: 'PAN',
            uan: 'UAN',
            employee_name: 'Employee Name',
            employeeName: 'Employee Name',
            employment_type: 'Employment Type',
            employmentType: 'Employment Type',
            name: 'Name',
            full_name: 'Full Name',
            fullName: 'Full Name',
            first_name: 'First Name',
            firstName: 'First Name',
            last_name: 'Last Name',
            lastName: 'Last Name',
            dob: 'Date of Birth',
            dateOfBirth: 'Date of Birth',
            date_of_birth: 'Date of Birth',
            father_name: 'Father\'s Name',
            fatherName: 'Father\'s Name',
            address: 'Address',
            pincode: 'Pincode',
            state: 'State',
            pan_number: 'PAN Number',
            panNumber: 'PAN Number',
            dlNumber: 'DL Number',
            dl_number: 'DL Number',
            driving_license_number: 'Driving License Number',
            drivingLicenseNumber: 'Driving License Number',
            voterIdNumber: 'Voter ID Number',
            voter_id_number: 'Voter ID Number',
            voter_id: 'Voter ID Number',
            epic_number: 'EPIC Number',
            // consent: 'Consent',
            membership_number: 'Membership Number',
            membership_no: 'Membership Number',
            membershipNumber: 'Membership Number',
            membershipNo: 'Membership Number',
            rc_number: 'RC Number',
            rcNumber: 'RC Number',
            owner_name: 'Owner Name',
            ownerName: 'Owner Name'
        };

        // Special handling for card_number based on report type
        let label;
        if (key === 'card_number' || key === 'cardNumber') {
            // Check if it's a Voter ID report
            const isVoterIdReport = reportType && (
                reportType.toLowerCase().includes('voter') ||
                reportType === 'Voter ID' ||
                reportType === 'Voter ID v1' ||
                reportType === 'Voter ID v2'
            );
            label = isVoterIdReport ? 'Voter ID Number' : 'Driving Licence Number';
        } else {
            label = fieldLabels[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
        }

        // Custom styling for Equifax 5-column layout
        const customStyle = isEquifax && index < 5 ? {
            width: '20%',
            minWidth: '150px',
            flexGrow: 0,
            flexShrink: 0
        } : {};

        return (
            <div key={key} className={columnClass} style={customStyle}>
                <label className="font-semibold text-700 text-sm">
                    {label}
                </label>
                <InputText
                    value={value}
                    readOnly
                    className="w-full mt-1 surface-100 border-1 border-300 text-900"
                />
            </div>
        );
    };

    // Get icon based on report type
    const getReportIcon = (reportType) => {
        const iconMap = {
            'Employment By UAN': 'pi-briefcase',
            'Employment By Mobile': 'pi-briefcase',
            'Experian Bureau Report': 'pi-chart-line',
            'Experian': 'pi-chart-line',
            'EXPERIAN_BUREAU_REPORT': 'pi-chart-line',
            'BUREAU_REPORT': 'pi-chart-line',
            'Equifax': 'pi-chart-line',
            'PAN Details': 'pi-credit-card',
            'pan_details': 'pi-credit-card',
            'Driving Licence': 'pi-car',
            'driving_licence': 'pi-car',
            'Voter ID': 'pi-id-card',
            'Voter ID v1': 'pi-id-card',
            'Voter ID v2': 'pi-id-card',
            'CA Membership': 'pi-verified',
            'RC Details': 'pi-file',
            'Mobile Bureau Lookup': 'pi-phone'
        };
        return iconMap[reportType] || 'pi-file';
    };

    const panelHeaderTemplate = () => (
        <div className="flex align-items-center justify-content-between w-full">
            <div className="flex align-items-center">
                <i className={`pi ${getReportIcon(reportType)} mr-3 text-primary text-xl`}></i>
                <div>
                    <h2 className="m-0 font-semibold text-900 mr-2 text-lg">
                        Input Data Used
                    </h2>
                </div>
            </div>
            {submittedAt && (
                <span className="text-700 text-sm">
                    <i className="pi pi-calendar mr-2"></i>
                    {submittedAt}
                </span>
            )}
        </div>
    );

    const panelIconsTemplate = () => {
        return null; // Remove the panel icons template as we'll add a separate button
    };

    return (
        <div className={`${className} mb-3`}>
            <Panel
                header={panelHeaderTemplate()}
                icons={panelIconsTemplate()}
                collapsed={!expanded}
                toggleable={false}
                className="shadow-2 border-round-xl border-none overflow-hidden"
                pt={{
                    header: {
                        className: 'p-4 border-bottom-3 border-white-alpha-10',
                        style: {
                            background: 'linear-gradient(135deg, var(--blue-50) 0%, var(--indigo-100) 100%)'
                        }
                    },
                    content: {
                        className: `${expanded ? 'p-4' : 'p-0'} surface-100 border-round-bottom-xl`
                    }
                }}
            >
                {expanded && (
                    <div>
                        {hasInputData ? (
                            <div className="formgrid grid" style={{ display: 'flex', flexWrap: 'wrap' }}>
                                {Object.entries(inputData).map(([key, value], index) =>
                                    renderFieldValue(key, value, index)
                                )}
                            </div>
                        ) : (
                            <div className="text-center py-4 text-600">
                                {/* <i className="pi pi-info-circle mr-2 text-blue-500"></i> */}
                                <span>No input data was stored for this report</span>
                                <div className="mt-2 text-sm text-500">
                                    Input data storage was recently implemented. Only new reports will have input data.
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </Panel>

            {showExpandButton && (
                <div className="flex justify-content-center relative z-1" style={{ margin: '-1.5rem 0 0.5rem 0' }}>
                    <div className="absolute w-full left-0 surface-border" style={{ height: '1px', top: '50%', zIndex: -1 }}></div>
                    <Button
                        onClick={() => setExpanded(!expanded)}
                        size="small"
                        rounded
                        label={expanded ? 'Collapse Form' : 'Expand Form'}
                        icon={`pi ${expanded ? 'pi-angle-double-up' : 'pi-angle-double-down'}`}
                    />
                </div>
            )}
        </div>
    );
};

export default InputDataCard;